import type { ElementType } from 'react';
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import { Dialog, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { useBangDaKhai, useTrangThaiBanSao } from '../../hooks/useNguon';
import type { GraphqlSource } from '../../types';
import { SoDoLuocDo, type BangLuocDo } from './SoDoLuocDo';

/**
 * Sơ đồ một nguồn: dòng chảy dữ liệu (các CHẶNG) + lược đồ quan hệ (các BẢNG) — port `SoDoNguon.tsx` native.
 *
 * Hoạt ảnh của lược đồ vẽ đường JOIN, không phải luồng đồng bộ; dải chặng phía trên trả lời câu còn lại: luồng đồng bộ
 * THẬT đi qua đâu — "số liệu tới lúc nào" đứng cạnh "vai nào chạy câu SQL".
 *
 * `/status` chỉ gọi khi kho đã dựng (`ready`): kho chưa dựng thì nó là 404, không phải rỗng.
 */
function ChangDoi({ icon: Icon, ten, chinh, phu, cuoi }: { icon: ElementType; ten: string; chinh: string; phu?: string; cuoi?: boolean }) {
  return (
    <>
      <li className="min-w-0 flex-1 basis-40 rounded-lg border border-default bg-surface px-3 py-2">
        <p className="flex items-center gap-1.5 text-xs font-medium text-content-secondary">
          <Icon aria-hidden className="text-content-muted" />
          {ten}
        </p>
        <p className="mt-0.5 truncate font-mono text-xs text-content-primary" title={chinh}>
          {chinh}
        </p>
        {phu ? <p className="truncate text-xs text-content-muted">{phu}</p> : null}
      </li>
      {cuoi ? null : (
        <li aria-hidden className="hidden items-center text-content-muted lg:flex">
          <ArrowRightOutlined />
        </li>
      )}
    </>
  );
}

export function SoDoNguon({ s, open, onClose }: { s: GraphqlSource | null; open: boolean; onClose: () => void }) {
  const { t, locale } = useT();
  const sourceId = s?.id ?? '';
  // Chỉ tải khi hộp thoại mở: tải sẵn cho mọi nguồn là năm lời gọi cho một lần dùng.
  const bang = useBangDaKhai(sourceId, open);
  const trangThai = useTrangThaiBanSao(sourceId, open && s?.status === 'ready');

  const ds = trangThai.data?.tables ?? [];
  const tongDong = ds.reduce((a, b) => a + b.rowCount, 0);
  const mocMoiNhat = ds
    .map((x) => x.syncedAt)
    .filter((x): x is string => Boolean(x))
    .sort()
    .at(-1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      centered
      width="96vw"
      // Ràng chiều cao ở `container` (cột dọc), `body` nhận phần còn lại: đặt cao ở `body` thì tiêu đề và lề cộng thêm,
      // hộp cao hơn màn hình và mất đúng dải nút thu phóng sát đáy.
      styles={{
        container: { height: '92vh', display: 'flex', flexDirection: 'column' },
        body: { flex: 1, minHeight: 0, overflow: 'hidden', maxHeight: 'none' },
      }}
      title={
        <span className="flex flex-col gap-0.5">
          <span>{t('troLyAiKetNoiSoDo.tieuDe', { ten: s?.name ?? '' })}</span>
          <span className="truncate font-mono text-xs font-normal text-content-muted">
            {s?.db.name
              ? t('troLyAiKetNoiSoDo.khoVaLuocDo', { kho: s.db.name, luocDo: s.db.schema ?? '—' })
              : (s?.endpoint ?? '')}
          </span>
        </span>
      }
    >
      <div className="flex h-full min-h-0 flex-col gap-3">
        {/* Năm chặng, mỗi chặng hỏng theo một cách khác nhau — nên chúng đứng RIÊNG. */}
        <ol className="flex flex-wrap items-stretch gap-2">
          <ChangDoi
            icon={s?.kind === 'dsn' ? DatabaseOutlined : CloudServerOutlined}
            ten={s?.kind === 'dsn' ? t('troLyAiKetNoiSoDo.changNguonDsn') : t('troLyAiKetNoiSoDo.changNguonGraphql')}
            chinh={s?.endpoint || '—'}
            phu={s?.luocDo ? t('troLyAiKetNoiSoDo.luocDoLa', { luocDo: s.luocDo }) : s?.dialect}
          />
          <ChangDoi
            icon={SyncOutlined}
            ten={t('troLyAiKetNoiSoDo.changDongBo')}
            chinh={
              s?.autoSync
                ? t('troLyAiKetNoiSoDo.moiPhut', { n: soVN(Math.round((s.syncIntervalSeconds ?? 0) / 60), locale) })
                : t('troLyAiKetNoiSoDo.thuCong')
            }
            phu={mocMoiNhat ? t('troLyAiKetNoiSoDo.soLieuToi', { luc: ngayGioVN(mocMoiNhat) }) : t('troLyAiKetNoiSoDo.chuaDongBo')}
          />
          <ChangDoi
            icon={DatabaseOutlined}
            ten={t('troLyAiKetNoiSoDo.changKho')}
            chinh={s?.db.name ?? t('troLyAiKetNoiSoDo.chuaDungKho')}
            phu={t('troLyAiKetNoiSoDo.khoGhi', { n: soVN(ds.length, locale), dong: soVN(tongDong, locale) })}
          />
          <ChangDoi
            icon={SafetyCertificateOutlined}
            ten={t('troLyAiKetNoiSoDo.changVaiDoc')}
            chinh={s?.db.readerUser ?? '—'}
            phu={t('troLyAiKetNoiSoDo.vaiDocGhi')}
          />
          <ChangDoi
            icon={RobotOutlined}
            ten={t('troLyAiKetNoiSoDo.changTroLy')}
            chinh={t('troLyAiKetNoiSoDo.sinhSql')}
            phu={t('troLyAiKetNoiSoDo.troLyGhi')}
            cuoi
          />
        </ol>

        <div className="min-h-0 flex-1">
          {bang.isPending && open ? (
            <Skeleton className="h-full w-full" />
          ) : bang.isError ? (
            <Empty
              icon={ApartmentOutlined}
              title={t('troLyAiKetNoiSoDo.loiTieuDe')}
              description={t('troLyAiKetNoiSoDo.loiMoTa')}
            />
          ) : (
            <SoDoLuocDo tables={(bang.data ?? []) as unknown as BangLuocDo[]} />
          )}
        </div>
      </div>
    </Dialog>
  );
}
